import Accordion from '@/components/Accordion';
import Container from '@/components/Container';
import HeadingOne from '@/components/Heading/HeadingOne';
import PageHead from '@/components/PageHead';
import PageLayout from '@/components/layout/page-layout';

import NextLink from 'next/link';

export default function Page() {
	return (
		<>
			<PageHead
				page={{
					title: 'Instituto Deixe Vivo - Doação de órgãos',
					keywords:
						'OSC, Instituto, Deixe Vivo, Doação de órgãos e tecidos, transplante, transplantes, transplantado, transplantada, família doadora, morte encefálica, renal, tecidos, ministério da saúde, lista de espera, ABTO, processos de doação, processo de doação de órgãos e tecido',
					colorScheme: 'light dark',
					description: 'Separamos algumas perguntas que são muito comuns quando o tema é doação de órgãos e tecidos. Confira!',
					robots: 'follow, index',
				}}
				og={{
					description: 'Separamos algumas perguntas que são muito comuns quando o tema é doação de órgãos e tecidos. Confira!',
					site_name: 'Instituto Deixe Vivo - Doação de órgãos',
					title: 'Instituto Deixe Vivo - Doação de órgãos',
					type: 'website',
					url: 'https://deixevivo.org.br',
					image: {
						alt: 'Logo do Instituto, ao lado esquerdo da imagem um rosto feliz e ao lado direito texto que diz Instituto Deixe Vivo',
						fbAdmins: 'instituto.deixevivo',
						src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681919729/InstitutoDeixeVivo/logos/logo-deixe-vivo-1200x1200.jpg',
						type: 'image/jpg',
						width: '1200',
						height: '1200',
					},
				}}
				twitter={{
					card: 'summary',
					description: 'Separamos algumas perguntas que são muito comuns quando o tema é doação de órgãos e tecidos. Confira!',
					site: 'Instituto Deixe Vivo - Doação de órgãos',
					title: 'Instituto Deixe Vivo - Doação de órgãos',
					url: 'https://deixevivo.org.br',
					image: {
						alt: 'Logo do Instituto, ao lado esquerdo da imagem um rosto feliz e ao lado direito texto que diz Instituto Deixe Vivo',
						src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681919729/InstitutoDeixeVivo/logos/logo-deixe-vivo-1200x1200.jpg',
					},
				}}
			/>
			<PageLayout>
				<Container className="py-8">
					<HeadingOne horizontalRow>
						Aprenda sobre doação de órgãos
					</HeadingOne>

					<p tabIndex={0} className="mt-4">
						De acordo com o Ministério da Saúde, em junho de 2022, existiam
						quase 60 mil pessoas na lista de espera para receber um transplante
						de órgão ou tecido. E segundo, a Associação Brasileira de
						Transplante de Órgãos (ABTO), um dos principais motivos da recusa
						das famílias para a doação, continua sendo a falta de informação ou
						informação equivocada sobre os processos de doação pós morte
						encefálica. Separamos algumas perguntas que são muito comuns quando
						o tema é doação de órgãos e tecidos. Confira!
					</p>

					<section
						className="mt-4"
						aria-label="faq de perguntas mais comuns sobre a doação de órgãos"
					>
						<Accordion
							className="text-black border-b-2 border-b-primary-dark text-sm lg:text-base"
							title="Por que falar sobre doação de órgãos?"
						>
							<p className="p-2 text-base">
								Porque é uma questão de saúde pública e, atualmente, há quase 60
								mil pessoas na lista de espera para realizar um transplante no
								Brasil.
							</p>
						</Accordion>
						<Accordion
							className="text-black border-b-2 border-b-primary-dark text-sm lg:text-base"
							title="Como faço para me tornar doador de órgãos?"
						>
							<p className="p-2 text-base">
								Converse com a sua família e avise sobre sua vontade. Não há
								nenhum documento que comprove a doação de órgãos, apenas sua
								família é quem pode autorizar a doação em caso de morte
								encefálica.
							</p>
						</Accordion>
						<Accordion
							className="text-black border-b-2 border-b-primary-dark text-sm lg:text-base"
							title="Quem pode ser doador de órgãos?"
						>
							<p className="p-2 text-base">
								Qualquer pessoa maior de 18 anos, que após a realização de
								exames e avaliação médica tenha condições adequadas de saúde
								para a doação. Importante: a doação em vida só pode ser
								realizada com doador até o 4º grau de parentesco, qualquer
								pessoa fora desse círculo, precisa de uma autorização judicial
								(com a doação pareada/cruzada em discussão, as leis podem
								mudar). Menores de idade e outras pessoas sem nenhum grau de
								parentesco podem se tornar doadoras, após constatada morte
								encefálica (caso a família até 2º grau de parentesco, autorize).
							</p>
						</Accordion>
						<Accordion
							className="text-black border-b-2 border-b-primary-dark text-sm lg:text-base"
							title="O que é morte encefálica?"
						>
							<p className="p-2 text-base">
								É o completo e irreversível comprometimento de todas as funções
								do cérebro (o cérebro morre). A morte encefálica é constatada
								por meio de exames e avaliação minuciosa da equipe médica, e se
								dá quando não há mais fluxo sanguíneo e nem atividade cerebral.
								Geralmente acontece, como resultado de severa agressão ou
								ferimento grave no órgão.
							</p>
						</Accordion>
						<Accordion
							className="text-black border-b-2 border-b-primary-dark text-sm lg:text-base"
							title="A doação de órgãos pode acontecer em vida?"
						>
							<p className="p-2 text-base">
								Sim. Os requisitos são: ser maior de 18 anos, ser parente até 4º
								grau de parentesco do receptor, ter condições adequadas de saúde
								(realizar exames e ser avaliado por um médico).
							</p>
						</Accordion>
						<Accordion
							className="text-black border-b-2 border-b-primary-dark text-sm lg:text-base"
							title="O que leva uma pessoa a precisar de um transplante?"
						>
							<p className="p-2 text-base">
								Doenças prévias, como a insuficiência renal crônica, cirrose
								hepática, doenças cardíacas, doenças pulmonares, entre outras. A
								necessidade de um transplante é sempre avaliada e indicada (ou
								não) pelos médicos que cuidam de cada paciente.
							</p>
						</Accordion>
						<Accordion
							className="text-black border-b-2 border-b-primary-dark text-sm lg:text-base"
							title="Quem não pode ser doador de órgãos após a morte? E em vida?"
						>
							<p className="p-2 text-base">
								Após a morte: pessoas sem constatação de morte encefálica,
								pessoas com órgãos comprometidos, pessoas que não foram
								autorizadas por suas famílias. Em vida: pessoas fora do círculo
								familiar (até o 4º grau de parentesco) e pessoas sem condições
								adequadas de saúde.
							</p>
						</Accordion>
						<Accordion
							className="text-black border-b-2 border-b-primary-dark text-sm lg:text-base"
							title="Como funciona a lista de espera por um transplante no Brasil?"
						>
							<p className="p-2 text-base">
								A lista de espera por um transplante é única em todo o país e o
								sistema é informatizado. Cada órgão tem uma fila específica,
								baseada na Lei nº 9.434/1997, no Decreto nº 2.268/1997 e na
								Portaria GM/MS nº 2.600/2009. E, ao contrário do que muitas
								pessoas imaginam, a ordem de inscrição não determina quem
								receberá o órgão primeiro. Mas, sim, as condições médicas como:
								compatibilidade dos grupos sanguíneos, tempo de espera e
								gravidade da doença. Ou seja, pacientes com maior risco de morte
								têm preferência.
							</p>
						</Accordion>
						<Accordion
							className="text-black border-b-2 border-b-primary-dark text-sm lg:text-base"
							title="Como é feita a cirurgia de retirada dos órgãos do doador?"
						>
							<p className="p-2 text-base">
								Tanto a retirada de órgãos de doador falecido, quanto de doador
								vivo é feita em centro cirúrgico, como qualquer outra cirurgia,
								por equipes de médicos cirurgiões autorizadas pelo Ministério da
								Saúde e que sejam treinados para esse procedimento. No caso de
								doador falecido, a cirurgia acontece após confirmada a morte
								encefálica, a família autorizar a doação e encontrarem um
								receptor compatível.
							</p>
						</Accordion>
						<Accordion
							className="text-black border-b-2 border-b-primary-dark text-sm lg:text-base"
							title="A família doadora tem algum custo com a doação?"
						>
							<p className="p-2 text-base">
								Não, a única responsabilidade da família doadora é autorizar (ou
								não) a doação.
							</p>
						</Accordion>
						<Accordion
							className="text-black border-b-2 border-b-primary-dark text-sm lg:text-base"
							title="Como fica o corpo após a retirada dos órgãos?"
						>
							<p className="p-2 text-base">
								Como antes da cirurgia, sem nenhuma deformidade (tanto doadores
								vivos quanto falecidos) e, no caso, dos doadores não-vivos, o
								velório e sepultamento pode acontecer da forma que a família
								preferir.
							</p>
						</Accordion>
						<Accordion
							className="text-primary border-b-2 border-b-primary-dark text-sm lg:text-base"
							title="Existe um sistema nacional que cuida dos transplantes?"
						>
							<p className="p-2 text-base">
								Sim. O Sistema Nacional de Transplantes (SNT) é responsável pela
								regulamentação, controle e monitoramento do processo de doação e
								transplantes realizados no país, com o objetivo de desenvolver o
								processo de doação, captação e distribuição de órgãos, tecidos e
								partes retiradas do corpo humano para fins terapêuticos.
							</p>
						</Accordion>

						<p className="mt-4">
							Caso você tenha alguma dúvida que não tenha sido esclarecida aqui,
							entre em contato conosco pelo&nbsp;
							<span>
								e-mail:&nbsp;
								<NextLink href="mailto:contato@deixeivivo.org.br">
									<strong>contato@deixevivo.org.br</strong>
								</NextLink>
							</span>
						</p>
					</section>
				</Container>
			</PageLayout>

			<style jsx global>{`
				.accordion-title svg {
					fill: var(--color-primary-dark);
				}
			`}</style>
		</>
	);
}

/*






*/
